import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Edit, FileText, Loader2, Plus, RefreshCw, Trash2, Save } from "lucide-react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import axios from "../../api/axios";

// Import unified PaginationSearchbar component
import PaginationSearchbar from "../../GlobalComponents/PaginationSearchbar";

// Import central theme classes from MasterCssClass.js
import {
  masterPanelCard,
  masterHeadingMain,
  masterTableWrap,
  masterTable,
  masterTableHead,
  masterTableRow,
  masterBtnBack,
  masterFormBtnSubmit,
  masterBadgeLive,
  masterBadgeInactive,
  masterActionBtnEdit,
  masterActionBtnDelete,
  masterPageContainer,
  masterHeaderContent,
  masterHeaderBrand,
  masterHeaderIconBox,
  masterHeaderActions,
  masterTableContainer,
  masterThCode,
  masterThName,
  masterThStatus,
  masterThManage,
  masterTdCode,
  masterTdName,
  masterTdStatus,
  masterTdManage,
  masterTdManageFlex,
  masterTdEmpty,
  masterSelect,
  masterInput,
  masterInputLabel,
  masterFormCard,
  masterFormFieldGroup,
  masterSubCard,
  masterInnerCard,
  masterActionButton,
  masterDeleteButton,
} from "../../GlobalComponents/MasterCssClass";

const toastErrorOnce = (msg) => toast.error(msg, { id: "one-error" });
const toastOkOnce = (msg) => toast.success(msg, { id: "one-success" });

const toggleInArray = (arr, value) => {
  const v = String(value || "").trim().toUpperCase();
  if (!v) return arr;
  return arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];
};

const keepOnlyValidSelections = (values, options) => {
  const allowed = new Set((Array.isArray(options) ? options : []).map((opt) => String(opt?.value || "").trim().toUpperCase()).filter(Boolean));
  return (Array.isArray(values) ? values : []).filter((value) => allowed.has(String(value || "").trim().toUpperCase()));
};

const isNoCategoryValue = (value) => {
  const v = String(value || "").trim().toUpperCase();
  return !v || v === "NO CATEGORY" || v === "NO_CATEGORY" || v === "NONE";
};

const normalizeUpper = (value) => String(value || "").trim().toUpperCase();
const getSubTopicName = (subTopic) =>
  typeof subTopic === "string" ? subTopic : String(subTopic?.subTopicName || subTopic?.name || "");
const getNestedTopicName = (nestedTopic) =>
  typeof nestedTopic === "string" ? nestedTopic : String(nestedTopic?.nestedTopicName || nestedTopic?.name || "");
const getNestedSubTopicName = (nestedSubTopic) =>
  typeof nestedSubTopic === "string" ? nestedSubTopic : String(nestedSubTopic?.nestedSubTopicName || nestedSubTopic?.name || "");
const defaultBuilderFilterOptions = () => ({ exams: [], stages: [], subjects: [], topics: [], subTopics: [], nestedTopics: [], nestedSubTopics: [] });
const GLOBAL_TARGET_KEY = "__GLOBAL__";
const makeSelectionTargetKey = ({
  examMasterId = "",
  examCategoryId = "",
  examStage = "",
  subjectId = "",
  topicName = "",
  subTopicName = "",
  nestedTopicName = "",
  nestedSubTopicName = "",
}) =>
  [
    normalizeUpper(examMasterId || "__ANY_EXAM__"),
    normalizeUpper(examCategoryId || "__ANY_CATEGORY__"),
    normalizeUpper(examStage || "__ANY_STAGE__"),
    normalizeUpper(subjectId),
    normalizeUpper(topicName || "__NONE__"),
    normalizeUpper(subTopicName || "__NONE__"),
    normalizeUpper(nestedTopicName || "__NONE__"),
    normalizeUpper(nestedSubTopicName || "__NONE__"),
  ].join("::");

const doesQuestionMatchTarget = (question, target) => {
  if (!target) return false;
  if (target.key === GLOBAL_TARGET_KEY) return true;

  const qSubjectId = normalizeUpper(question?.subjectId);
  const qTopicName = normalizeUpper(question?.topicName);
  const qSubTopicName = normalizeUpper(question?.subTopicName || "__NONE__");
  const qNestedTopicName = normalizeUpper(question?.nestedTopicName || "__NONE__");
  const qNestedSubTopicName = normalizeUpper(question?.nestedSubTopicName || "__NONE__");
  const qExamMasterId = normalizeUpper(question?.examMasterId);
  const qExamCategoryId = isNoCategoryValue(question?.examCategoryId) ? "" : normalizeUpper(question?.examCategoryId);
  const qExamStage = normalizeUpper(question?.examStage);

  

  if (target.examMasterId && normalizeUpper(target.examMasterId) !== qExamMasterId) return false;
  if (target.examCategoryId && normalizeUpper(target.examCategoryId) !== qExamCategoryId) return false;
  if (target.examStage && normalizeUpper(target.examStage) !== qExamStage) return false;
  if (normalizeUpper(target.subjectId) !== qSubjectId) return false;

  if (target.nestedSubTopicName && target.nestedSubTopicName !== "__NONE__") {
    return (
      normalizeUpper(target.topicName) === qTopicName &&
      normalizeUpper(target.subTopicName || "__NONE__") === qSubTopicName &&
      normalizeUpper(target.nestedTopicName || "__NONE__") === qNestedTopicName &&
      normalizeUpper(target.nestedSubTopicName) === qNestedSubTopicName
    );
  }

  if (target.nestedTopicName && target.nestedTopicName !== "__NONE__") {
    return (
      normalizeUpper(target.topicName) === qTopicName &&
      normalizeUpper(target.subTopicName || "__NONE__") === qSubTopicName &&
      normalizeUpper(target.nestedTopicName) === qNestedTopicName
    );
  }

  // If target specifically requires a subtopic, enforce it
  if (target.subTopicName && target.subTopicName !== "__NONE__") {
    return normalizeUpper(target.topicName) === qTopicName && normalizeUpper(target.subTopicName) === qSubTopicName;
  }

  // If target is at the Topic level, match the topic name and allow any subtopic under it
  if (target.topicName) {
    return normalizeUpper(target.topicName) === qTopicName;
  }

  return true;
};
const getQuestionTargetKey = (question) =>
  makeSelectionTargetKey({
    examMasterId: question?.examMasterId,
    examCategoryId: question?.examCategoryId,
    examStage: question?.examStage,
    subjectId: question?.subjectId,
    topicName: question?.topicName,
    subTopicName: question?.subTopicName,
    nestedTopicName: question?.nestedTopicName,
    nestedSubTopicName: question?.nestedSubTopicName,
  });

const defaultTargetConfig = () => ({ selectionType: "AUTO", questionCount: 10 });
const dedupeValues = (values) => Array.from(new Set((Array.isArray(values) ? values : []).filter(Boolean)));
const decodeStoredHtml = (value) => {
  if (typeof window === "undefined") return String(value || "");
  const input = String(value || "");
  const textArea = document.createElement("textarea");
  textArea.innerHTML = input;
  return textArea.value || input;
};
const toHtml = (value) => decodeStoredHtml(value).replace(/\n/g, "<br />");

const MultiCheck = ({ title, options, values, onToggle, disabled = false }) => (
  <div className={`${masterSubCard} p-2`}>
    <p className={masterInputLabel}>{title}</p>
    <div className="max-h-[120px] overflow-y-auto custom-scrollbar space-y-1">
      {options.map((opt) => (
        <label key={`${title}-${opt.value}`} className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
          <input type="checkbox" checked={values.includes(opt.value)} onChange={() => onToggle(opt.value)} disabled={disabled} className="rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
          <span>{opt.label}</span>
        </label>
      ))}
      {options.length === 0 && <p className="text-[11px] text-slate-400 italic">No options</p>}
    </div>
  </div>
);

const MockTestMaster = () => {
  const navigate = useNavigate();
  const [exams, setExams] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [sets, setSets] = useState([]);
  const [questionBankRows, setQuestionBankRows] = useState([]);
  const [builderFilterOptions, setBuilderFilterOptions] = useState(defaultBuilderFilterOptions());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showVisibilityModal, setShowVisibilityModal] = useState(false);
  const [selectedRowForVisibility, setSelectedRowForVisibility] = useState(null);

  // DEFAULT VIEW MODE STATE: Set to true by default so form page opens first
  const [showFormPage, setShowFormPage] = useState(true);

  const [showPaperView, setShowPaperView] = useState(false);
  const [paperSet, setPaperSet] = useState(null);
  const [paperQuestions, setPaperQuestions] = useState([]);
  const [paperLoading, setPaperLoading] = useState(false);
  const [expandedSetIds, setExpandedSetIds] = useState([]);
  const [filters, setFilters] = useState({ examMasterId: "", examStage: "", examCategoryId: "", subjectId: "" });

const [showPublishModal, setShowPublishModal] = useState(false);
  const [selectedRowForPublish, setSelectedRowForPublish] = useState(null);
 const [publishForm, setPublishForm] = useState({
    testDate: "",
    testTime: "",
    durationMinutes: "",
  });
  // --- CONTROL STATES ---
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [editId, setEditId] = useState("");
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [form, setForm] = useState({
    questionSetId: "",
    examMasterId: "",
    examStage: "",
    examCategoryId: "",
    subjectId: "",
    examMasterIds: [],
    examStages: [],
    examCategoryIds: [],
    subjectIds: [],
    topicNames: [],
    subTopicNames: [],
    nestedTopicNames: [],
    nestedSubTopicNames: [],
    testDate: new Date().toISOString().slice(0, 10),
    durationMinutes: 30,
    status: "ACTIVE",
    questionIds: [],
  });
  const [selectionConfigByKey, setSelectionConfigByKey] = useState({});
  const [questionFilter, setQuestionFilter] = useState({ marks: "", negativeMarks: "" });
  const [showOnlySetQuestions, setShowOnlySetQuestions] = useState(true);
  const [showSelectedQuestionsOnly, setShowSelectedQuestionsOnly] = useState(false);
  const richViewClass = "text-xs text-slate-800 leading-relaxed [&_table]:w-full [&_table]:border-collapse [&_table]:border [&_th]:border [&_td]:border [&_th]:p-1 [&_td]:p-1 [&_th]:bg-slate-100";
  const listMathRef = useRef(null);
  const paperMathRef = useRef(null);

const handleOpenPublishModal = (row) => {
    setSelectedRowForPublish(row);
    setPublishForm({
      testDate: row.testDate ? new Date(row.testDate).toISOString().slice(0, 10) : "",
      testTime: row.testTime || "",
      durationMinutes: row.durationMinutes || "",
    });
    setShowPublishModal(true);
  };

const handleSavePublishSettings = async () => {
    if (!selectedRowForPublish) return;
    try {
      await axios.patch(`/master/mock-test/select-for-student/${selectedRowForPublish._id}`, {
        selected: true,
        testType: "MOCK",
        testDate: publishForm.testDate,
        testTime: publishForm.testTime,
        durationMinutes: parseInt(publishForm.durationMinutes, 10) || 30,
      });
      toastOkOnce("MOCK TEST SCHEDULE SAVED AND ACTIVATED");
      setShowPublishModal(false);
      setSelectedRowForPublish(null);
      fetchSets();
    } catch (err) {
      toastErrorOnce(err?.response?.data?.message || "FAILED TO SAVE SCHEDULE");
    }
  };

  const typesetMath = (root) => {
    if (!root) return;
    if (!window.MathJax || !window.MathJax.typesetPromise) {
      setTimeout(() => typesetMath(root), 200);
      return;
    }
    if (window.MathJax.typesetClear) window.MathJax.typesetClear([root]);
    window.MathJax.typesetPromise([root]);
  };

  const hasSetStage = useMemo(
    () => sets.some((row) => String(row.examStage || "").trim() || (Array.isArray(row.examStages) && row.examStages.length)),
    [sets]
  );
  const hasSetTopic = useMemo(
    () => sets.some((row) => String(row.topicName || "").trim() || (Array.isArray(row.topicNames) && row.topicNames.length)),
    [sets]
  );
  const hasSetSubTopic = useMemo(
    () => sets.some((row) => String(row.subTopicName || "").trim() || (Array.isArray(row.subTopicNames) && row.subTopicNames.length)),
    [sets]
  );
  const hasSetNestedTopic = useMemo(
    () => sets.some((row) => String(row.nestedTopicName || "").trim() || (Array.isArray(row.nestedTopicNames) && row.nestedTopicNames.length)),
    [sets]
  );
  const hasSetNestedSubTopic = useMemo(
    () => sets.some((row) => String(row.nestedSubTopicName || "").trim() || (Array.isArray(row.nestedSubTopicNames) && row.nestedSubTopicNames.length)),
    [sets]
  );

  const filteredCategories = useMemo(() => {
    if (!filters.examMasterId) return [];
    return categories.filter((cat) => String(cat.examCode || "").toUpperCase() === String(filters.examMasterId || "").toUpperCase());
  }, [categories, filters.examMasterId]);
  const filterNeedsCategory = useMemo(() => filteredCategories.length > 0, [filteredCategories]);
  const filterStageOptions = useMemo(
    () =>
      Array.from(
        new Set(
          filteredCategories
            .flatMap((cat) => [ ...(Array.isArray(cat.examStages) ? cat.examStages : []), cat.examStage ])
            .map((stage) => String(stage || "").trim().toUpperCase())
            .filter(Boolean)
        )
      ),
    [filteredCategories]
  );
  const filterNeedsStage = useMemo(() => filterNeedsCategory && filterStageOptions.length > 0, [filterNeedsCategory, filterStageOptions]);

  const filteredSubjects = useMemo(() => {
    if (!filters.examMasterId) return [];
    if (filterNeedsCategory && !filters.examCategoryId) return [];
    return subjects.filter(
      (s) =>
        String(s.examCode || "").toUpperCase() === String(filters.examMasterId || "").toUpperCase() &&
        (
          filterNeedsCategory
            ? String(s.catId || "").toUpperCase() === String(filters.examCategoryId || "").toUpperCase()
            : isNoCategoryValue(s.catId)
        ) &&
        (!filters.examStage || String(s.examStage || "").toUpperCase() === String(filters.examStage || "").toUpperCase())
    );
  }, [subjects, filters.examMasterId, filters.examCategoryId, filters.examStage, filterNeedsCategory]);

  const selectedSubjectRows = useMemo(
    () =>
      subjects.filter((row) => {
        const examCode = normalizeUpper(row.examCode);
        const categoryId = normalizeUpper(row.catId);
        const stage = normalizeUpper(row.examStage);
        return (
          form.subjectIds.includes(normalizeUpper(row.syllabusId)) &&
          (!form.examMasterIds.length || form.examMasterIds.includes(examCode)) &&
          (!form.examCategoryIds.length || form.examCategoryIds.includes(categoryId)) &&
          (!form.examStages.length || form.examStages.includes(stage))
        );
      }),
    [subjects, form.subjectIds, form.examMasterIds, form.examCategoryIds, form.examStages]
  );

const selectionTargets = useMemo(() => {
    const seen = new Set();
    const targets = [];

    selectedSubjectRows.forEach((subjectRow) => {
      const examMasterId = normalizeUpper(subjectRow.examCode);
      const examCategoryId = isNoCategoryValue(subjectRow.catId) ? "" : normalizeUpper(subjectRow.catId);
      const examStage = normalizeUpper(subjectRow.examStage);
      const subjectId = normalizeUpper(subjectRow.syllabusId);
      const subjectName = String(subjectRow.subjectName || "").trim();
      const subjectTopics = Array.isArray(subjectRow.topics) ? subjectRow.topics : [];

      // Find which globally checked topics actually belong to THIS specific subject
      const validTopicsForThisSubject = form.topicNames.filter((topicValue) => {
        const topicName = normalizeUpper(topicValue);
        return subjectTopics.some((item) => normalizeUpper(item?.topicName) === topicName);
      });

      if (validTopicsForThisSubject.length > 0) {
        validTopicsForThisSubject.forEach((topicValue) => {
          const topicName = normalizeUpper(topicValue);
          const topicMeta = subjectTopics.find(
            (item) => normalizeUpper(item?.topicName) === topicName
          );
          const subTopics = (Array.isArray(topicMeta?.subTopics) ? topicMeta.subTopics : [])
            .map(getSubTopicName)
            .map(normalizeUpper)
            .filter(Boolean);

          const validSubTopicsForThisTopic = form.subTopicNames.filter((sub) =>
            subTopics.includes(normalizeUpper(sub))
          );

          if (validSubTopicsForThisTopic.length > 0) {
            validSubTopicsForThisTopic.forEach((subTopicValue) => {
              const subTopicName = normalizeUpper(subTopicValue);
              const subTopicMeta = (Array.isArray(topicMeta?.subTopics) ? topicMeta.subTopics : []).find(
                (item) => normalizeUpper(getSubTopicName(item)) === subTopicName
              );
              const nestedTopics = (Array.isArray(subTopicMeta?.nestedTopics) ? subTopicMeta.nestedTopics : subTopicMeta?.childTopics || [])
                .map(getNestedTopicName)
                .map(normalizeUpper)
                .filter(Boolean);
              const validNestedTopicsForThisSubTopic = form.nestedTopicNames.filter((nestedTopic) =>
                nestedTopics.includes(normalizeUpper(nestedTopic))
              );

              if (validNestedTopicsForThisSubTopic.length > 0) {
                validNestedTopicsForThisSubTopic.forEach((nestedTopicValue) => {
                  const nestedTopicName = normalizeUpper(nestedTopicValue);
                  const nestedTopicMeta = (Array.isArray(subTopicMeta?.nestedTopics) ? subTopicMeta.nestedTopics : subTopicMeta?.childTopics || []).find(
                    (item) => normalizeUpper(getNestedTopicName(item)) === nestedTopicName
                  );
                  const nestedSubTopics = (Array.isArray(nestedTopicMeta?.nestedSubTopics) ? nestedTopicMeta.nestedSubTopics : nestedTopicMeta?.childTopics || [])
                    .map(getNestedSubTopicName)
                    .map(normalizeUpper)
                    .filter(Boolean);
                  const validNestedSubTopicsForThisNestedTopic = form.nestedSubTopicNames.filter((nestedSubTopic) =>
                    nestedSubTopics.includes(normalizeUpper(nestedSubTopic))
                  );

                  if (validNestedSubTopicsForThisNestedTopic.length > 0) {
                    validNestedSubTopicsForThisNestedTopic.forEach((nestedSubTopicValue) => {
                      const nestedSubTopicName = normalizeUpper(nestedSubTopicValue);
                      const key = makeSelectionTargetKey({ examMasterId, examCategoryId, examStage, subjectId, topicName, subTopicName, nestedTopicName, nestedSubTopicName });
                      if (seen.has(key)) return;
                      seen.add(key);
                      targets.push({
                        key,
                        kind: "NESTED_SUBTOPIC",
                        examMasterId,
                        examCategoryId,
                        examStage,
                        subjectId,
                        subjectName,
                        topicName,
                        subTopicName,
                        nestedTopicName,
                        nestedSubTopicName,
                        title: nestedSubTopicName,
                        subtitle: `${subjectName || subjectId} / ${topicName} / ${subTopicName} / ${nestedTopicName}`,
                      });
                    });
                    return;
                  }

                  const key = makeSelectionTargetKey({ examMasterId, examCategoryId, examStage, subjectId, topicName, subTopicName, nestedTopicName });
                  if (seen.has(key)) return;
                  seen.add(key);
                  targets.push({
                    key,
                    kind: "NESTED_TOPIC",
                    examMasterId,
                    examCategoryId,
                    examStage,
                    subjectId,
                    subjectName,
                    topicName,
                    subTopicName,
                    nestedTopicName,
                    nestedSubTopicName: "",
                    title: nestedTopicName,
                    subtitle: `${subjectName || subjectId} / ${topicName} / ${subTopicName}`,
                  });
                });
                return;
              }

              const key = makeSelectionTargetKey({ examMasterId, examCategoryId, examStage, subjectId, topicName, subTopicName });
              if (seen.has(key)) return;
              seen.add(key);
              targets.push({
                key,
                kind: "SUBTOPIC",
                examMasterId,
                examCategoryId,
                examStage,
                subjectId,
                subjectName,
                topicName,
                subTopicName,
                nestedTopicName: "",
                nestedSubTopicName: "",
                title: subTopicName,
                subtitle: `${subjectName || subjectId} / ${topicName}`,
              });
            });
          } else {
            const key = makeSelectionTargetKey({ examMasterId, examCategoryId, examStage, subjectId, topicName });
            if (seen.has(key)) return;
            seen.add(key);
            targets.push({
              key,
              kind: "TOPIC",
              examMasterId,
              examCategoryId,
              examStage,
              subjectId,
              subjectName,
              topicName,
              subTopicName: "",
              nestedTopicName: "",
              nestedSubTopicName: "",
              title: topicName,
              subtitle: subjectName || subjectId,
            });
          }
        });
      } else {
        // If this subject has NO global topics checked, check if it has its own topics available.
        // If it does not use topics, treat it as a valid Subject-level card.
        // If it DOES use topics but none were checked, you can either show subject level or match its first topic.
        const key = makeSelectionTargetKey({ examMasterId, examCategoryId, examStage, subjectId });
        if (seen.has(key)) return;
        seen.add(key);
        targets.push({
          key,
          kind: "SUBJECT",
          examMasterId,
          examCategoryId,
          examStage,
          subjectId,
          subjectName,
          topicName: "",
          subTopicName: "",
          nestedTopicName: "",
          nestedSubTopicName: "",
          title: subjectName || subjectId,
          subtitle: "Subject Level",
        });
      }
    });

    if (targets.length) return targets;
    if (!questionBankRows.length || !form.examMasterIds.length) return [];

    return [{
      key: GLOBAL_TARGET_KEY,
      kind: "GLOBAL",
      subjectId: form.subjectIds[0] || "",
      topicName: form.topicNames[0] || "",
      subTopicName: form.subTopicNames[0] || "",
      nestedTopicName: form.nestedTopicNames[0] || "",
      nestedSubTopicName: form.nestedSubTopicNames[0] || "",
      title: "Filtered Questions",
      subtitle: [
        form.examMasterIds.join(", "),
        form.examStages.join(", "),
        form.subjectIds.join(", "),
        form.topicNames.join(", "),
        form.subTopicNames.join(", "),
        form.nestedTopicNames.join(", "),
        form.nestedSubTopicNames.join(", "),
      ].filter(Boolean).join(" / ") || "Current filter",
    }];
  }, [selectedSubjectRows, form.topicNames, form.subTopicNames, form.nestedTopicNames, form.nestedSubTopicNames, questionBankRows.length, form.examMasterIds, form.examStages, form.subjectIds]);

  const fetchMasters = async () => {
    const [examRes, catRes, subRes] = await Promise.allSettled([axios.get("/master/exam"), axios.get("/master/category/all"), axios.get("/master/syllabus/all")]);
    setExams(examRes.status === "fulfilled" ? (Array.isArray(examRes.value.data) ? examRes.value.data : examRes.value.data?.data || []) : []);
    setCategories(catRes.status === "fulfilled" ? (Array.isArray(catRes.value.data) ? catRes.value.data : catRes.value.data?.data || []) : []);
    setSubjects(subRes.status === "fulfilled" ? (Array.isArray(subRes.value.data) ? subRes.value.data : subRes.value.data?.data || []) : []);
  };

  const fetchBuilderFilterOptions = async () => {
    try {
      const params = new URLSearchParams();
      params.set("status", "ACTIVE");
      if (form.examMasterIds.length) params.set("examMasterId", form.examMasterIds.join(","));
      if (form.examStages.length) params.set("examStage", form.examStages.join(","));
      if (form.subjectIds.length) params.set("subjectId", form.subjectIds.join(","));
      if (form.topicNames.length) params.set("topicName", form.topicNames.join(","));
      if (form.subTopicNames.length) params.set("subTopicName", form.subTopicNames.join(","));
      if (form.nestedTopicNames.length) params.set("nestedTopicName", form.nestedTopicNames.join(","));
      const res = await axios.get(`/master/mock-test/question-bank-filters?${params.toString()}`);
      setBuilderFilterOptions(res.data?.data || defaultBuilderFilterOptions());
    } catch {
      setBuilderFilterOptions(defaultBuilderFilterOptions());
    }
  };

  const fetchSets = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.examMasterId) params.set("examMasterId", filters.examMasterId);
      if (filters.examStage) params.set("examStage", filters.examStage);
      if (filters.examCategoryId) params.set("examCategoryId", filters.examCategoryId);
      if (filters.subjectId) params.set("subjectId", filters.subjectId);
      params.set("page", "1");
      params.set("limit", "10000");
      const res = await axios.get(`/master/mock-test/all?${params.toString()}`);
      setSets(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch {
      toastErrorOnce("FAILED TO LOAD QUESTION SETS");
    } finally {
      setLoading(false);
    }
  };

const resetBuilder = async () => {
    setEditId("");
    setIsViewOnly(false);
    setQuestionBankRows([]);
    setShowOnlySetQuestions(true);
    setShowSelectedQuestionsOnly(false);
    setQuestionFilter({ marks: "", negativeMarks: "" });
    setForm({
      questionSetId: "", // ID will be generated by backend upon save
      examMasterId: "",
      examStage: "",
      examCategoryId: "",
      subjectId: "",
      examMasterIds: [],
      examStages: [],
      examCategoryIds: [],
      subjectIds: [],
      topicNames: [],
      subTopicNames: [],
      nestedTopicNames: [],
      nestedSubTopicNames: [],
      testDate: new Date().toISOString().slice(0, 10),
      durationMinutes: 30,
      status: "ACTIVE",
      questionIds: [],
    });
    setSelectionConfigByKey({});
  };

  const openAddForm = async () => {
    await resetBuilder();
    setShowFormPage(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    fetchMasters();
    resetBuilder();
  }, []);

  useEffect(() => {
    fetchSets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.examMasterId, filters.examStage, filters.examCategoryId, filters.subjectId]);

  useEffect(() => {
    typesetMath(listMathRef.current);
  }, [questionBankRows]);

  useEffect(() => {
    if (showPaperView) typesetMath(paperMathRef.current);
  }, [showPaperView, paperQuestions]);

  useEffect(() => {
    if (!showFormPage) return;
    fetchBuilderFilterOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFormPage, form.examMasterIds.join(","), form.examStages.join(","), form.subjectIds.join(","), form.topicNames.join(","), form.subTopicNames.join(","), form.nestedTopicNames.join(",")]);

  useEffect(() => {
    if (!showFormPage) return;
    setForm((prev) => {
      const nextExamMasterIds = keepOnlyValidSelections(prev.examMasterIds, builderFilterOptions.exams);
      const stageOptionsVisible = nextExamMasterIds.length > 0 ? builderFilterOptions.stages : [];
      const nextExamStages = keepOnlyValidSelections(prev.examStages, stageOptionsVisible);
      const subjectOptionsVisible = nextExamMasterIds.length > 0 && (stageOptionsVisible.length === 0 || nextExamStages.length > 0)
        ? builderFilterOptions.subjects
        : [];
      const nextSubjectIds = keepOnlyValidSelections(prev.subjectIds, subjectOptionsVisible);
      const topicOptionsVisible = nextSubjectIds.length > 0 ? builderFilterOptions.topics : [];
      const nextTopicNames = keepOnlyValidSelections(prev.topicNames, topicOptionsVisible);
      const subTopicOptionsVisible = nextTopicNames.length > 0 ? builderFilterOptions.subTopics : [];
      const nextSubTopicNames = keepOnlyValidSelections(prev.subTopicNames, subTopicOptionsVisible);
      const nestedTopicOptionsVisible = nextSubTopicNames.length > 0 ? builderFilterOptions.nestedTopics : [];
      const nextNestedTopicNames = keepOnlyValidSelections(prev.nestedTopicNames, nestedTopicOptionsVisible);
      const nestedSubTopicOptionsVisible = nextNestedTopicNames.length > 0 ? builderFilterOptions.nestedSubTopics : [];
      const nextNestedSubTopicNames = keepOnlyValidSelections(prev.nestedSubTopicNames, nestedSubTopicOptionsVisible);

      if (
        nextExamMasterIds.length === prev.examMasterIds.length &&
        nextExamStages.length === prev.examStages.length &&
        nextSubjectIds.length === prev.subjectIds.length &&
        nextTopicNames.length === prev.topicNames.length &&
        nextSubTopicNames.length === prev.subTopicNames.length &&
        nextNestedTopicNames.length === prev.nestedTopicNames.length &&
        nextNestedSubTopicNames.length === prev.nestedSubTopicNames.length
      ) {
        return prev;
      }

      return {
        ...prev,
        examMasterIds: nextExamMasterIds,
        examStages: nextExamStages,
        subjectIds: nextSubjectIds,
        topicNames: nextTopicNames,
        subTopicNames: nextSubTopicNames,
        nestedTopicNames: nextNestedTopicNames,
        nestedSubTopicNames: nextNestedSubTopicNames,
      };
    });
  }, [showFormPage, builderFilterOptions]);

  useEffect(() => {
    setSelectionConfigByKey((prev) => {
      const next = {};
      selectionTargets.forEach((target) => {
        next[target.key] = prev[target.key] || defaultTargetConfig();
      });
      const sameKeys = Object.keys(next).length === Object.keys(prev).length &&
        Object.keys(next).every((key) => prev[key] && prev[key].selectionType === next[key].selectionType && prev[key].questionCount === next[key].questionCount);
      return sameKeys ? prev : next;
    });
  }, [selectionTargets]);

  const selectionTargetMap = useMemo(
    () => new Map(selectionTargets.map((target) => [target.key, target])),
    [selectionTargets]
  );
  const hasGlobalSelectionTarget = useMemo(
    () => selectionTargets.some((target) => target.key === GLOBAL_TARGET_KEY),
    [selectionTargets]
  );

const questionTargetKeyById = useMemo(() => {
    const map = new Map();
    questionBankRows.forEach((row) => {
      const qbId = normalizeUpper(row.questionBankId || row._id);

      // Find the exact matching target card for this question using the updated flexible matcher
      const matchedTarget = selectionTargets.find((target) => doesQuestionMatchTarget(row, target));

      map.set(qbId, matchedTarget?.key || getQuestionTargetKey(row));
    });
    return map;
  }, [questionBankRows, selectionTargets]);

  const visibleQuestionBankRows = useMemo(() => {
    const targetRows =
      editId || isViewOnly || !selectionTargets.length || hasGlobalSelectionTarget
        ? questionBankRows
        : questionBankRows.filter((row) =>
            selectionTargets.some((target) => doesQuestionMatchTarget(row, target))
          );

    if (!showSelectedQuestionsOnly) return targetRows;
    const selectedIds = new Set((Array.isArray(form.questionIds) ? form.questionIds : []).map(normalizeUpper));
    return targetRows.filter((row) => selectedIds.has(normalizeUpper(row.questionBankId || row._id)));
  }, [editId, isViewOnly, questionBankRows, selectionTargets, hasGlobalSelectionTarget, showSelectedQuestionsOnly, form.questionIds]);

  useEffect(() => {
    if (!showFormPage) return;
    if (editId && showOnlySetQuestions) return;
    const hasAllRequiredFilters = Boolean(form.examMasterIds.length);
    if (!hasAllRequiredFilters) {
      setQuestionBankRows([]);
      return;
    }
    fetchQuestionBankRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFormPage, editId, showOnlySetQuestions, form.examMasterIds.join(","), form.examStages.join(","), form.subjectIds.join(","), form.topicNames.join(","), form.subTopicNames.join(","), form.nestedTopicNames.join(","), form.nestedSubTopicNames.join(","), questionFilter.marks, questionFilter.negativeMarks]);

  const getTargetConfig = (targetKey) => selectionConfigByKey[targetKey] || defaultTargetConfig();
  const getSelectedCountForTarget = (targetKey, questionIds = form.questionIds) =>
    (Array.isArray(questionIds) ? questionIds : []).filter(
      (id) => questionTargetKeyById.get(normalizeUpper(id)) === targetKey
    ).length;
  const removeTargetQuestionIds = (questionIds, targetKey) =>
    (Array.isArray(questionIds) ? questionIds : []).filter(
      (id) => questionTargetKeyById.get(normalizeUpper(id)) !== targetKey
    );
  const keepQuestionIdsForTargets = useCallback(
    (questionIds, allowedTargetKeys) =>
      (Array.isArray(questionIds) ? questionIds : []).filter((id) => {
        const targetKey = questionTargetKeyById.get(normalizeUpper(id));
        return targetKey && allowedTargetKeys.has(targetKey);
      }),
    [questionTargetKeyById]
  );
  const setTargetConfig = (targetKey, patch) => {
    setSelectionConfigByKey((prev) => ({
      ...prev,
      [targetKey]: {
        ...(prev[targetKey] || defaultTargetConfig()),
        ...(typeof patch === "function" ? patch(prev[targetKey] || defaultTargetConfig()) : patch),
      },
    }));
  };

  useEffect(() => {
    if (!showFormPage || editId || isViewOnly) return;
    const allowedTargetKeys = new Set(selectionTargets.map((target) => target.key));
    setForm((prev) => {
      const nextQuestionIds = keepQuestionIdsForTargets(prev.questionIds, allowedTargetKeys);
      return nextQuestionIds.length === prev.questionIds.length
        ? prev
        : { ...prev, questionIds: nextQuestionIds };
    });
  }, [showFormPage, editId, isViewOnly, selectionTargets, keepQuestionIdsForTargets]);

  useEffect(() => {
    if (!form.questionIds.length && showSelectedQuestionsOnly) {
      setShowSelectedQuestionsOnly(false);
    }
  }, [form.questionIds.length, showSelectedQuestionsOnly]);

const fetchQuestionBankRows = async () => {
    if (!form.examMasterIds.length) {
      return setQuestionBankRows([]);
    }
    try {
      const params = new URLSearchParams();
      params.set("flat", "true");
      params.set("status", "ACTIVE");
      params.set("page", "1");
      params.set("limit", "1000");
      params.set("examMasterId", form.examMasterIds.join(","));

      if (form.examCategoryIds.length) params.set("examCategoryId", form.examCategoryIds.join(","));
      if (form.subjectIds.length) params.set("subjectId", form.subjectIds.join(","));
      if (form.examStages.length) params.set("examStage", form.examStages.join(","));
      if (form.topicNames.length) params.set("topicName", form.topicNames.join(","));
      if (form.subTopicNames.length) params.set("subTopicName", form.subTopicNames.join(","));
      if (form.nestedTopicNames.length) params.set("nestedTopicName", form.nestedTopicNames.join(","));
      if (form.nestedSubTopicNames.length) params.set("nestedSubTopicName", form.nestedSubTopicNames.join(","));

      // Pass unique subject IDs from selection targets instead of strict global subtopic filters
      const activeSubjects = Array.from(new Set(selectionTargets.map(t => t.subjectId).filter(Boolean)));
      if (activeSubjects.length) {
        params.set("subjectId", activeSubjects.join(","));
      }

      if (String(questionFilter.marks || "").trim() !== "") params.set("marks", String(questionFilter.marks).trim());
      if (String(questionFilter.negativeMarks || "").trim() !== "") params.set("negativeMarks", String(questionFilter.negativeMarks).trim());

      const res = await axios.get(`/master/question-bank/all?${params.toString()}`);
      setQuestionBankRows(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch {
      setQuestionBankRows([]);
    }
  };

  const handleViewOrEdit = async (id, canEdit) => {
    try {
      const res = await axios.get(`/master/mock-test/set/${id}`);
      const row = res.data?.data;
      if (!row) return;
      setShowPaperView(false);
      setEditId(canEdit ? row._id : "");
      setIsViewOnly(!canEdit);
      setShowOnlySetQuestions(true);
      setForm({
        questionSetId: row.questionSetId || "",
        examMasterId: row.examMasterId || "",
        examStage: row.examStage || "",
        examCategoryId: row.examCategoryId || "",
        subjectId: row.subjectId || "",
        examMasterIds: Array.isArray(row.examMasterIds) && row.examMasterIds.length ? row.examMasterIds : [row.examMasterId].filter(Boolean),
        examStages: Array.isArray(row.examStages) && row.examStages.length ? row.examStages : [row.examStage].filter(Boolean),
        examCategoryIds: Array.isArray(row.examCategoryIds) && row.examCategoryIds.length ? row.examCategoryIds : [row.examCategoryId].filter(Boolean),
        subjectIds: Array.isArray(row.subjectIds) && row.subjectIds.length ? row.subjectIds : [row.subjectId].filter(Boolean),
        topicNames: Array.isArray(row.topicNames) && row.topicNames.length ? row.topicNames : [row.topicName].filter(Boolean),
        subTopicNames: Array.isArray(row.subTopicNames) && row.subTopicNames.length ? row.subTopicNames : [row.subTopicName].filter(Boolean),
        nestedTopicNames: Array.isArray(row.nestedTopicNames) && row.nestedTopicNames.length ? row.nestedTopicNames : [row.nestedTopicName].filter(Boolean),
        nestedSubTopicNames: Array.isArray(row.nestedSubTopicNames) && row.nestedSubTopicNames.length ? row.nestedSubTopicNames : [row.nestedSubTopicName].filter(Boolean),
        testDate: row.testDate ? new Date(row.testDate).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10),
        durationMinutes: Math.max(parseInt(row.durationMinutes, 10) || 30, 1),
        status: row.status || "ACTIVE",
        questionIds: (row.questionIds || []).map((q) => (typeof q === "string" ? q : q.questionBankId || q._id)),
      });
      setSelectionConfigByKey(
        (Array.isArray(row.questionBreakdown) ? row.questionBreakdown : []).reduce((acc, item) => {
          const key = makeSelectionTargetKey({
            examMasterId: row.examMasterId,
            examCategoryId: row.examCategoryId,
            examStage: row.examStage,
            subjectId: row.subjectId,
            topicName: item?.topicName,
            subTopicName: item?.subTopicName,
            nestedTopicName: item?.nestedTopicName,
            nestedSubTopicName: item?.nestedSubTopicName,
          });
          acc[key] = { selectionType: "MANUAL", questionCount: Number(item?.count) || 0 };
          return acc;
        }, {})
      );
      setQuestionBankRows(Array.isArray(row.questionDetails) ? row.questionDetails : []);
      setShowFormPage(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      toastErrorOnce("FAILED TO LOAD SET");
    }
  };

  const openPaperView = async (id) => {
    setPaperLoading(true);
    try {
      const res = await axios.get(`/master/mock-test/set/${id}`);
      const row = res.data?.data;
      if (!row) return;
      setPaperSet(row);
      setPaperQuestions(Array.isArray(row.questionDetails) ? row.questionDetails : []);
      setShowPaperView(true);
      setShowFormPage(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      toastErrorOnce("FAILED TO LOAD SET");
    } finally {
      setPaperLoading(false);
    }
  };

const autoPickQuestions = async (target) => {
    if (isViewOnly) return toastErrorOnce("VIEW MODE ENABLED");
    if (!form.examMasterIds.length) return toastErrorOnce("SELECT EXAM");
    try {
      const targetConfig = getTargetConfig(target.key);
      const params = new URLSearchParams();

      params.set("examMasterId", target.examMasterId || form.examMasterIds.join(","));
      if (target.examCategoryId) {
        params.set("examCategoryId", target.examCategoryId);
      } else if (form.examCategoryIds.length) {
        params.set("examCategoryId", form.examCategoryIds.join(","));
      }
      if (target.examStage) {
        params.set("examStage", target.examStage);
      } else if (form.examStages.length) {
        params.set("examStage", form.examStages.join(","));
      }

      // STRICTLY use only this specific card's target parameters (prevents cross-contamination of topic/subtopic)
      if (target.subjectId) params.set("subjectId", target.subjectId);
      if (target.topicName) params.set("topicName", target.topicName);
      if (target.subTopicName && target.subTopicName !== "__NONE__") {
        params.set("subTopicName", target.subTopicName);
      }
      if (target.nestedTopicName && target.nestedTopicName !== "__NONE__") {
        params.set("nestedTopicName", target.nestedTopicName);
      }
      if (target.nestedSubTopicName && target.nestedSubTopicName !== "__NONE__") {
        params.set("nestedSubTopicName", target.nestedSubTopicName);
      }

      params.set("count", String(targetConfig.questionCount || 10));

      const res = await axios.get(`/master/mock-test/suggest-questions?${params.toString()}`);
      const data = Array.isArray(res.data?.data) ? res.data.data : [];

      const fetchedIds = data.map((q) => String(q.questionBankId || q._id || "").trim().toUpperCase()).filter(Boolean);
      const fetchedTargetKeyById = new Map(
        data.map((q) => [String(q.questionBankId || q._id || "").trim().toUpperCase(), target.key])
      );

      setQuestionBankRows((prev) => {
        const byId = new Map((Array.isArray(prev) ? prev : []).map((q) => [String(q.questionBankId || q._id || "").trim().toUpperCase(), q]));
        data.forEach((q) => {
          const id = String(q.questionBankId || q._id || "").trim().toUpperCase();
          if (id) byId.set(id, q);
        });
        return Array.from(byId.values());
      });

      setForm((prev) => {
        const otherTargetIds = (Array.isArray(prev.questionIds) ? prev.questionIds : []).filter(
          (id) => {
            const normalizedId = String(id).trim().toUpperCase();
            return (fetchedTargetKeyById.get(normalizedId) || questionTargetKeyById.get(normalizedId)) !== target.key;
          }
        );
        return {
          ...prev,
          questionIds: dedupeValues([...otherTargetIds, ...fetchedIds]),
        };
      });

      toastOkOnce(`SELECTED ${fetchedIds.length} QUESTIONS FOR ${target.title}`);
    } catch (err) {
      toastErrorOnce(err?.response?.data?.message || "AUTO PICK FAILED");
    }
  };
  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (isViewOnly) return;
    if (!form.examMasterIds.length) return toastErrorOnce("SELECT EXAM");
    if (!form.testDate) return toastErrorOnce("SELECT TEST DATE");
    if ((parseInt(form.durationMinutes, 10) || 0) < 1) return toastErrorOnce("SET VALID TEST DURATION");
    if (!editId) {
      for (const target of selectionTargets) {
        const expectedCount = Number(getTargetConfig(target.key).questionCount) || 0;
        const actualCount = getSelectedCountForTarget(target.key);
        if (expectedCount < 1) return toastErrorOnce(`SET QUESTION COUNT FOR ${target.title}`);
        if (actualCount !== expectedCount) {
          return toastErrorOnce(`${target.title}: SELECT ${expectedCount} QUESTIONS`);
        }
      }
    }
    if (!form.questionIds.length) return toastErrorOnce("SELECT QUESTIONS");
    setSaving(true);
    try {
      await axios.post("/master/mock-test/upsert", {
        id: editId || undefined,
        questionSetId: form.questionSetId,
        examMasterId: form.examMasterIds[0],
        examStage: form.examStages[0] || undefined,
        examCategoryId: form.examCategoryIds[0] || undefined,
        subjectId: form.subjectIds[0],
        examMasterIds: form.examMasterIds,
        examStages: form.examStages,
        examCategoryIds: form.examCategoryIds,
        subjectIds: form.subjectIds,
        topicNames: form.topicNames,
        subTopicNames: form.subTopicNames,
        nestedTopicNames: form.nestedTopicNames,
        nestedSubTopicNames: form.nestedSubTopicNames,
        selectionTargets: selectionTargets.map((target) => ({
          examMasterId: target.examMasterId,
          examCategoryId: target.examCategoryId,
          examStage: target.examStage,
          subjectId: target.subjectId,
          topicName: target.topicName,
          subTopicName: target.subTopicName,
          nestedTopicName: target.nestedTopicName,
          nestedSubTopicName: target.nestedSubTopicName,
        })),
        testDate: form.testDate,
        durationMinutes: Math.max(parseInt(form.durationMinutes, 10) || 30, 1),
        status: form.status,
        isActive: form.status === "ACTIVE",
        isSelectedForAttempt: false,
        questionIds: form.questionIds,
      });
      toastOkOnce(editId ? "SET UPDATED" : "SET CREATED");
      fetchSets();
      await resetBuilder();
      setShowFormPage(true);
      setIsViewOnly(false);
    } catch (err) {
      toastErrorOnce(err?.response?.data?.message || "SAVE FAILED");
    } finally {
      setSaving(false);
    }
  };

 const handleOpenVisibilityModal = (row) => {
    setSelectedRowForVisibility(row);
    setShowVisibilityModal(true);
  };

const selectForStudentWithCategory = async (testType) => {
  if (!selectedRowForVisibility) return;
  try {
    await axios.patch(`/master/mock-test/select-for-student/${selectedRowForVisibility._id}`, {
      selected: true,
      testType: testType
    });
    toastOkOnce(`SET MADE VISIBLE AS ${testType === 'PRACTICE' ? 'PRACTICE SET' : 'MOCK TEST'} FOR STUDENTS`);
    setShowVisibilityModal(false);
    setSelectedRowForVisibility(null);
    fetchSets();
  } catch (err) {
    toastErrorOnce(err?.response?.data?.message || "FAILED TO SELECT SET");
  }
};

  const hideForStudent = async (id) => {
    try {
      await axios.patch(`/master/mock-test/select-for-student/${id}`, { selected: false });
      toastOkOnce("SET HIDDEN FROM STUDENTS");
      fetchSets();
    } catch (err) {
      toastErrorOnce(err?.response?.data?.message || "FAILED TO HIDE SET");
    }
  };

  const removeSet = async (id) => {
    if (!window.confirm("ARE YOU SURE YOU WANT TO DELETE THIS RECORD?")) return;
    try {
      await axios.delete(`/master/mock-test/${id}`);
      toastOkOnce("DELETED SUCCESSFULLY");
      fetchSets();
    } catch {
      toastErrorOnce("DELETE FAILED");
    }
  };

  // --- ALPHABETICAL FIRST-LETTER FILTER ---
  const filteredData = sets.filter((item) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    const startsWithFirstLetter = (value) => {
      if (!value) return false;
      return value.trim().toLowerCase().startsWith(term);
    };
    return (
      startsWithFirstLetter(item.questionSetId) ||
      startsWithFirstLetter(item.examName) ||
      startsWithFirstLetter(item.subjectName) ||
      startsWithFirstLetter(item.status || "ACTIVE")
    );
  });

  // --- PAGINATION SLICING ---
  const isAll = rowsPerPage === "ALL" || rowsPerPage === -1;
  const startIndex = isAll ? 0 : (currentPage - 1) * rowsPerPage;
  const paginatedData = isAll
    ? filteredData
    : filteredData.slice(startIndex, startIndex + rowsPerPage);
  const setTableColumnCount = 8 + (hasSetStage ? 1 : 0) + (hasSetTopic ? 1 : 0) + (hasSetSubTopic ? 1 : 0) + (hasSetNestedTopic ? 1 : 0) + (hasSetNestedSubTopic ? 1 : 0);

  return (
    <div className={`${masterPageContainer} h-full flex flex-col overflow-y-auto custom-scrollbar`}>
      {!showFormPage && !showPaperView && (
        <>
          {/* HEADER CARD */}
          <div className={masterPanelCard}>
            <div className={masterHeaderContent}>
              <div className={masterHeaderBrand}>
                <div className={masterHeaderIconBox}>
                  <FileText size={13} />
                </div>
                <h1 className={masterHeadingMain}>Mock Test Sets</h1>
              </div>

              <div className={masterHeaderActions}>
                <button
                  type="button"
                  onClick={fetchSets}
                  className={masterBtnBack}
                  title="Refresh Records"
                >
                  <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
                  <span>SYNC</span>
                </button>

                <button
                  type="button"
                  onClick={openAddForm}
                  className={masterFormBtnSubmit}
                >
                  <Plus size={12} strokeWidth={3} />
                  <span>ADD QUESTION SET</span>
                </button>
              </div>
            </div>
          </div>

          {/* FILTERS BAR */}
          <div className={masterPanelCard}>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
              <select value={filters.examMasterId} onChange={(e) => { setFilters((p) => ({ ...p, examMasterId: e.target.value, examStage: "", examCategoryId: "", subjectId: "" })); setCurrentPage(1); }} className={masterSelect}>
                <option value="">-- ALL EXAMS --</option>
                {exams.map((ex) => <option key={ex.examCode} value={ex.examCode}>{ex.examName}</option>)}
              </select>
              {filterNeedsCategory && (
                <select value={filters.examCategoryId} onChange={(e) => { const categoryId = e.target.value; setFilters((p) => ({ ...p, examCategoryId: categoryId, examStage: "", subjectId: "" })); setCurrentPage(1); }} className={masterSelect}>
                  <option value="">-- ALL CATEGORIES --</option>
                  {filteredCategories.map((cat) => <option key={cat.catId} value={cat.catId}>{cat.catName}</option>)}
                </select>
              )}
              {filterNeedsStage && (
                <select value={filters.examStage} onChange={(e) => { setFilters((p) => ({ ...p, examStage: e.target.value, subjectId: "" })); setCurrentPage(1); }} className={masterSelect}>
                  <option value="">-- ALL STAGES --</option>
                  {filterStageOptions.map((stage) => <option key={stage} value={stage}>{stage}</option>)}
                </select>
              )}
              <select value={filters.subjectId} onChange={(e) => { setFilters((p) => ({ ...p, subjectId: e.target.value })); setCurrentPage(1); }} className={masterSelect}>
                <option value="">-- ALL SUBJECTS --</option>
                {filteredSubjects.map((sub) => <option key={sub.syllabusId} value={sub.syllabusId}>{sub.subjectName}</option>)}
              </select>
            </div>
          </div>

          {/* COMBINED SEARCH & PAGINATION BAR */}
          <PaginationSearchbar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            searchPlaceholder="SEARCH SET ID, EXAM, OR SUBJECT BY FIRST LETTER..."
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={setRowsPerPage}
            totalItems={filteredData.length}
          />

          {/* TABLE CONTAINER */}
          <div className={`${masterTableContainer} flex-1 min-h-0 flex flex-col`}>
            <div className={`${masterTableWrap} overflow-x-auto overflow-y-auto custom-scrollbar flex-1 max-h-[calc(100vh-280px)]`}>
              <table className={masterTable}>
                <thead>
                  <tr className={masterTableHead}>
                    <th className={masterThCode}>SL NO</th>
                    <th className={masterThName}>DATE</th>
                    <th className={masterThName}>EXAM</th>
                    {hasSetStage && <th className={masterThName}>STAGE</th>}
                    <th className={masterThName}>CATEGORY</th>
                    <th className={masterThName}>SUBJECT</th>
                    {hasSetTopic && <th className={masterThName}>TOPIC</th>}
                    {hasSetSubTopic && <th className={masterThName}>SUBTOPIC</th>}
                    {hasSetNestedTopic && <th className={masterThName}>NESTED TOPIC</th>}
                    {hasSetNestedSubTopic && <th className={masterThName}>NESTED SUBTOPIC</th>}
                    <th className={masterThStatus}>STATUS</th>
                    <th className={masterThStatus}>STUDENT</th>
                    <th className={masterThManage}>ACTIONS</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {loading ? (
                    <tr>
                      <td colSpan={setTableColumnCount} className={masterTdEmpty}>
                        Loading...
                      </td>
                    </tr>
                  ) : paginatedData.length > 0 ? (
                    paginatedData.map((row,index) => (
                      <React.Fragment key={row._id}>
                        <tr className={masterTableRow}>
                          <td className={masterTdCode}>{startIndex + index + 1}</td>
                          <td className={masterTdName}>
                            {row.testDate ? new Date(row.testDate).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '-') : '---'}
                          </td>
                          <td className={masterTdName}>{row.examName}</td>
                          {hasSetStage && <td className={masterTdName}>{(Array.isArray(row.examStages) ? row.examStages.join(", ") : row.examStage) || "---"}</td>}
                          <td className={masterTdName}>{row.categoryName}</td>
                          <td className={masterTdName}>{row.subjectName}</td>
                          {hasSetTopic && <td className={masterTdName}>{(Array.isArray(row.topicNames) ? row.topicNames.join(", ") : row.topicName) || "---"}</td>}
                          {hasSetSubTopic && <td className={masterTdName}>{(Array.isArray(row.subTopicNames) ? row.subTopicNames.join(", ") : row.subTopicName) || "---"}</td>}
                          {hasSetNestedTopic && <td className={masterTdName}>{(Array.isArray(row.nestedTopicNames) ? row.nestedTopicNames.join(", ") : row.nestedTopicName) || "---"}</td>}
                          {hasSetNestedSubTopic && <td className={masterTdName}>{(Array.isArray(row.nestedSubTopicNames) ? row.nestedSubTopicNames.join(", ") : row.nestedSubTopicName) || "---"}</td>}
                          <td className={masterTdStatus}>
                            <span className={row.status === "INACTIVE" ? masterBadgeInactive : masterBadgeLive}>
                              {row.status}
                            </span>
                          </td>
                        <td className="p-2 text-xs font-semibold">
                          <div className="flex items-center gap-1.5">
                            {/* Practice Set Toggle Button */}
                            {row.practiceSetView ? (
                              <button
                                onClick={async () => {
                                  await axios.patch(`/master/mock-test/select-for-student/${row._id}`, { selected: false, testType: "PRACTICE" });
                                  fetchSets();
                                }}
                                className="px-2 py-1 rounded-full bg-blue-600 text-white hover:bg-red-500 text-[9px] font-bold uppercase transition"
                                title="Hide from Practice Set"
                              >
                                Practice: ON
                              </button>
                            ) : (
                              <button
                                onClick={async () => {
                                  await axios.patch(`/master/mock-test/select-for-student/${row._id}`, { selected: true, testType: "PRACTICE" });
                                  fetchSets();
                                }}
                                className="px-2 py-1 rounded-full bg-slate-100 text-slate-700 hover:bg-blue-600 hover:text-white text-[9px] font-bold uppercase border border-slate-200 transition"
                                title="Enable as Practice Set"
                              >
                                Practice: OFF
                              </button>
                            )}

                         
                            <button
                              type="button"
                              onClick={() => handleOpenPublishModal(row)}
                              className={`px-2 py-1 rounded-full text-[9px] font-bold uppercase border transition ${
                                row.mockTestView 
                                  ? "bg-emerald-600 text-white hover:bg-red-500 border-emerald-600" 
                                  : "bg-slate-100 text-slate-700 hover:bg-emerald-600 hover:text-white border-slate-200"
                              }`}
                              title="Configure Mock Test Schedule"
                            >
                              {row.mockTestView ? "Mock: ON" : "Mock: OFF"}
                            </button>
                          </div>
                        </td>
                          <td className={masterTdManage}>
                            <div className={masterTdManageFlex}>
                              <button type="button" onClick={() => setExpandedSetIds((prev) => (prev.includes(row._id) ? prev.filter((id) => id !== row._id) : [...prev, row._id]))} className={masterActionBtnEdit} title="Details">
                                <FileText size={13} />
                              </button>
                              <button type="button" onClick={() => openPaperView(row._id)} className={masterActionBtnEdit} title="Paper View">
                                <FileText size={13} />
                              </button>
                              <button type="button" onClick={() => handleViewOrEdit(row._id, true)} className={masterActionBtnEdit} title="Edit">
                                <Edit size={13} />
                              </button>
                              <button type="button" onClick={() => removeSet(row._id)} className={masterActionBtnDelete} title="Delete">
                                <Trash2 size={13} />
                              </button>
                            </div>
                          </td>
                        </tr>
                        {expandedSetIds.includes(row._id) && (
                          <tr className="bg-slate-50/70">
                            <td colSpan={setTableColumnCount} className="p-2">
                              <div className="rounded-xl border border-slate-200 overflow-hidden bg-white">
                                <table className="w-full text-center text-[10px]">
                                  <thead className="bg-slate-100 text-slate-700 font-bold uppercase">
                                    <tr>
                                      <th className="p-2">Exam</th>
                                      <th className="p-2">Category</th>
                                      <th className="p-2">Subject</th>
                                      <th className="p-2">Topic</th>
                                      <th className="p-2">SubTopic</th>
                                      <th className="p-2">Nested Topic</th>
                                      <th className="p-2">Nested SubTopic</th>
                                      <th className="p-2 text-right">Questions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {(Array.isArray(row.questionBreakdown) ? row.questionBreakdown : []).length > 0 ? (
                                      row.questionBreakdown.map((b, idx) => (
                                        <tr key={`${row._id}-b-${idx}`} className="border-t border-slate-100">
                                          <td className="p-2">{b.examName || "---"}</td>
                                          <td className="p-2">{b.categoryName || "---"}</td>
                                          <td className="p-2">{b.subjectName || "---"}</td>
                                          <td className="p-2">{b.topicName || "---"}</td>
                                          <td className="p-2">{b.subTopicName || "---"}</td>
                                          <td className="p-2">{b.nestedTopicName || "---"}</td>
                                          <td className="p-2">{b.nestedSubTopicName || "---"}</td>
                                          <td className="p-2 text-right font-bold">{b.count || 0}</td>
                                        </tr>
                                      ))
                                    ) : (
                                      <tr><td colSpan="8" className="p-2 text-center text-slate-400">No breakdown available</td></tr>
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={setTableColumnCount} className={masterTdEmpty}>
                        No Records Found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {showPaperView && (
        <section className="px-1 pb-3" ref={paperMathRef}>
          <div className={masterFormCard}>
            <div className="flex items-center justify-between mb-3 border-b pb-2">
              <div>
                <h2 className="text-base font-bold uppercase tracking-tight text-slate-900">Question Paper View</h2>
                <p className="text-xs font-bold text-slate-500">Set ID: <span className="text-blue-600">{paperSet?.questionSetId || "--"}</span></p>
              </div>
              <button type="button" onClick={() => { setShowPaperView(false); setPaperSet(null); setPaperQuestions([]); }} className={masterBtnBack}>Back to List</button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs text-slate-700 mb-3 bg-slate-50 p-2.5 rounded-xl border border-slate-200 font-bold">
              <div><span className="text-slate-500 uppercase">Exam:</span> {paperSet?.examName || "---"}</div>
              <div><span className="text-slate-500 uppercase">Category:</span> {paperSet?.categoryName || "---"}</div>
              <div><span className="text-slate-500 uppercase">Subject:</span> {paperSet?.subjectName || "---"}</div>
            </div>

            {paperLoading ? (
              <div className="p-10 text-center text-slate-300 font-bold uppercase">Loading...</div>
            ) : (
              <div className="space-y-3">
                {paperQuestions.map((q, idx) => (
                  <div key={q._id || q.questionBankId || idx} className={masterSubCard}>
                    <div className="flex items-center justify-between text-xs text-slate-500 font-bold">
                      <div className="text-slate-900">Q{idx + 1}</div>
                      <div>Marks: <span className="text-slate-900">{q.marks ?? "--"}</span> | Neg: <span className="text-slate-900">{q.negativeMarks ?? "--"}</span></div>
                    </div>

                    <div className={richViewClass} dangerouslySetInnerHTML={{ __html: toHtml(q.questionText || "---") }} />

                    {(Array.isArray(q.questionImages) ? q.questionImages : []).length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {q.questionImages.map((img, i) => (
                          <img key={`qimg-${idx}-${i}`} src={img} alt="question" className="h-16 w-16 object-cover rounded border border-slate-200" />
                        ))}
                      </div>
                    )}

                    <div className="space-y-1">
                      {["A", "B", "C", "D"].map((opt) => (
                        <div key={`${idx}-${opt}`} className={masterInnerCard}>
                          <div className="text-[10px] font-bold text-slate-600 mb-0.5">Option {opt}</div>
                          <div className={richViewClass} dangerouslySetInnerHTML={{ __html: toHtml(q[`option${opt}`] || "---") }} />
                          {(Array.isArray(q.optionImages?.[opt]) ? q.optionImages?.[opt] : []).length > 0 && (
                            <div className="mt-1 flex flex-wrap gap-1">
                              {q.optionImages[opt].map((img, i) => (
                                <img key={`opt-${idx}-${opt}-${i}`} src={img} alt={`option-${opt}`} className="h-14 w-14 object-cover rounded border border-slate-200" />
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    <div className="text-xs font-bold text-emerald-700 bg-emerald-50 p-2 rounded-xl border border-emerald-100">
                      Correct Answer: <span className="text-slate-900">{q.correctOption || "--"}</span>
                    </div>

                    <div className={richViewClass + " bg-slate-50 p-2.5 rounded-xl border border-slate-200"}>
                      <div className="text-[10px] font-bold text-slate-500 uppercase mb-1">Explanation</div>
                      <div dangerouslySetInnerHTML={{ __html: toHtml(q.explanationText || "---") }} />
                    </div>

                    {(Array.isArray(q.explanationImages) ? q.explanationImages : []).length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {q.explanationImages.map((img, i) => (
                          <img key={`exp-${idx}-${i}`} src={img} alt="explanation" className="h-16 w-16 object-cover rounded border border-slate-200" />
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                {paperQuestions.length === 0 && <div className="p-10 text-center text-slate-300 font-bold uppercase text-xs">No questions in this set.</div>}
              </div>
            )}
          </div>
        </section>
      )}

{showFormPage && (
        <section className="px-1 pb-3">
          <div className={`${masterPanelCard} p-1 mb-1 sticky top-0 z-20 shadow-md`}>
            <div className="flex items-center justify-between gap-1 w-full">
              <div className={masterHeaderBrand}>
                <div className={masterHeaderIconBox}>
                  <Edit size={13} />
                </div>
                <h1 className={`${masterHeadingMain} whitespace-nowrap`}>{isViewOnly ? "View Question Set" : editId ? "Edit Question Set" : "Create Question Set"}</h1>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {!isViewOnly && (
                  <button type="submit" form="mock-test-form" disabled={saving} className={`${masterFormBtnSubmit} whitespace-nowrap`}>
                    {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                    <span>{editId ? "Update Set" : "Create Set"}</span>
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setShowFormPage(false)}
                  className={`${masterFormBtnSubmit} whitespace-nowrap`}
                >
                  <Plus size={12} strokeWidth={3} />
                  <span>VIEW LIST</span>
                </button>
              </div>
            </div>
          </div>

          <form id="mock-test-form" onSubmit={submit} className={masterFormCard}>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
              <div className={masterFormFieldGroup}>
                <label className={masterInputLabel}>Test Date</label>
                <input disabled={isViewOnly} type="date" value={form.testDate} onChange={(e) => setForm((p) => ({ ...p, testDate: e.target.value }))} className={`${masterInput} disabled:bg-slate-100`} />
              </div>
              <div className={masterFormFieldGroup}>
                <label className={masterInputLabel}>Test Duration (Minutes)</label>
                <input disabled={isViewOnly} type="number" min="1" value={form.durationMinutes} onChange={(e) => setForm((p) => ({ ...p, durationMinutes: Math.max(parseInt(e.target.value, 10) || 1, 1) }))} className={`${masterInput} disabled:bg-slate-100`} />
              </div>
              <div className={masterFormFieldGroup}>
                <label className={masterInputLabel}>Status</label>
                <select disabled={isViewOnly} value={form.status} onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))} className={`${masterSelect} disabled:bg-slate-100`}>
                  <option value="ACTIVE">ACTIVE</option>
                  <option value="INACTIVE">INACTIVE</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-3">
                <MultiCheck
                  title="Exam"
                  disabled={isViewOnly}
                  values={form.examMasterIds}
                  onToggle={(v) => setForm((p) => ({ ...p, examMasterIds: toggleInArray(p.examMasterIds, v), examStages: [], subjectIds: [], topicNames: [], subTopicNames: [], nestedTopicNames: [], nestedSubTopicNames: [] }))}
                  options={builderFilterOptions.exams}
                />
              {form.examMasterIds.length > 0 && builderFilterOptions.stages.length > 0 && (
                <MultiCheck
                  title="Stage"
                  disabled={isViewOnly}
                  values={form.examStages}
                  onToggle={(v) => setForm((p) => ({ ...p, examStages: toggleInArray(p.examStages, v), subjectIds: [], topicNames: [], subTopicNames: [], nestedTopicNames: [], nestedSubTopicNames: [] }))}
                  options={builderFilterOptions.stages}
                />
              )}
              {form.examMasterIds.length > 0 && (builderFilterOptions.stages.length === 0 || form.examStages.length > 0) && (
                <MultiCheck
                  title="Subject"
                  disabled={isViewOnly}
                  values={form.subjectIds}
                  onToggle={(v) => setForm((p) => ({ ...p, subjectIds: toggleInArray(p.subjectIds, v), topicNames: [], subTopicNames: [], nestedTopicNames: [], nestedSubTopicNames: [] }))}
                  options={builderFilterOptions.subjects}
                />
              )}
              {form.subjectIds.length > 0 && (
                <MultiCheck
                  title="Topic"
                  disabled={isViewOnly}
                  values={form.topicNames}
                  onToggle={(v) => setForm((p) => ({ ...p, topicNames: toggleInArray(p.topicNames, v), subTopicNames: [], nestedTopicNames: [], nestedSubTopicNames: [] }))}
                  options={builderFilterOptions.topics}
                />
              )}
              {form.topicNames.length > 0 && builderFilterOptions.subTopics.length > 0 && (
                <MultiCheck
                  title="SubTopic"
                  disabled={isViewOnly}
                  values={form.subTopicNames}
                  onToggle={(v) => setForm((p) => ({ ...p, subTopicNames: toggleInArray(p.subTopicNames, v), nestedTopicNames: [], nestedSubTopicNames: [] }))}
                  options={builderFilterOptions.subTopics}
                />
              )}
              {form.subTopicNames.length > 0 && builderFilterOptions.nestedTopics.length > 0 && (
                <MultiCheck
                  title="Nested Topic"
                  disabled={isViewOnly}
                  values={form.nestedTopicNames}
                  onToggle={(v) => setForm((p) => ({ ...p, nestedTopicNames: toggleInArray(p.nestedTopicNames, v), nestedSubTopicNames: [] }))}
                  options={builderFilterOptions.nestedTopics}
                />
              )}
              {form.nestedTopicNames.length > 0 && builderFilterOptions.nestedSubTopics.length > 0 && (
                <MultiCheck
                  title="Nested SubTopic"
                  disabled={isViewOnly}
                  values={form.nestedSubTopicNames}
                  onToggle={(v) => setForm((p) => ({ ...p, nestedSubTopicNames: toggleInArray(p.nestedSubTopicNames, v) }))}
                  options={builderFilterOptions.nestedSubTopics}
                />
              )}
            </div>

            {editId && questionBankRows.length > 0 && (
              <div className="mb-3">
                <button type="button" onClick={async () => { if (showOnlySetQuestions) { await fetchQuestionBankRows(); setShowOnlySetQuestions(false); } else { const res = await axios.get(`/master/mock-test/set/${editId}`); const row = res.data?.data; setQuestionBankRows(Array.isArray(row?.questionDetails) ? row.questionDetails : []); setShowOnlySetQuestions(true); } }} className={masterFormBtnSubmit}>{showOnlySetQuestions ? "Show Filtered Questions" : "Show Set Questions"}</button>
              </div>
            )}

            {!editId && selectionTargets.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
                {selectionTargets.map((target) => {
                  const targetConfig = getTargetConfig(target.key);
                  const selectedCount = getSelectedCountForTarget(target.key);
                  return (
                    <div key={target.key} className={masterSubCard}>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-xs font-bold text-slate-900 uppercase">{target.title}</p>
                          <p className="text-[10px] font-semibold text-slate-500">{target.subtitle}</p>
                        </div>
                        <div className="text-xs font-bold text-slate-700">{selectedCount}/{targetConfig.questionCount}</div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                        <button
                          type="button"
                          onClick={() => { setTargetConfig(target.key, { selectionType: "AUTO" }); setForm((prev) => ({ ...prev, questionIds: removeTargetQuestionIds(prev.questionIds, target.key) })); }}
                          className={`flex items-center justify-center h-8 rounded-lg border text-[10px] font-bold uppercase transition ${targetConfig.selectionType === "AUTO" ? "border-blue-500 bg-blue-50 text-blue-700 shadow-sm" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}
                        >
                          Auto Select
                        </button>

                        <button
                          type="button"
                          onClick={() => { setTargetConfig(target.key, { selectionType: "MANUAL" }); setForm((prev) => ({ ...prev, questionIds: removeTargetQuestionIds(prev.questionIds, target.key) })); }}
                          className={`flex items-center justify-center h-8 rounded-lg border text-[10px] font-bold uppercase transition ${targetConfig.selectionType === "MANUAL" ? "border-emerald-500 bg-emerald-50 text-emerald-700 shadow-sm" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}
                        >
                          Manual Select
                        </button>

                        <div className="flex items-center gap-1 h-8 px-2 rounded-lg border border-slate-200 bg-white shadow-sm">
                          <label className="whitespace-nowrap text-[9px] font-bold uppercase text-slate-400">QTY</label>
                        <input 
                          type="number" 
                          min="1" 
                          value={targetConfig.questionCount} 
                          onChange={(e) => {
                            const val = e.target.value;
                            setTargetConfig(target.key, { questionCount: val === "" ? "" : Math.max(parseInt(val, 10) || 0, 1) });
                          }} 
                          className="w-full bg-transparent text-xs font-bold outline-none text-slate-800"
                        />
                        </div>
                      </div>
                      {targetConfig.selectionType === "AUTO" && (
                        <button type="button" onClick={() => autoPickQuestions(target)} className={`${masterFormBtnSubmit} w-full py-1.5 text-[10px]`}>
                          Auto Pick {targetConfig.questionCount} Questions
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <div className={masterSubCard}>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-2">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-xs font-bold text-slate-700 uppercase">Total Questions: <span className="text-slate-900">{visibleQuestionBankRows.length}</span></p>
                  <p className="text-xs font-bold text-slate-700 uppercase">Selected Questions: <span className="text-blue-600">{form.questionIds.length}</span></p>
                  {!editId && (
                    <button
                      type="button"
                      onClick={() => setShowSelectedQuestionsOnly((prev) => !prev)}
                      disabled={!form.questionIds.length}
                      className={`h-8 px-3 rounded-lg border text-[10px] font-bold uppercase transition disabled:opacity-50 disabled:cursor-not-allowed ${showSelectedQuestionsOnly ? "border-blue-500 bg-blue-50 text-blue-700" : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"}`}
                    >
                      {showSelectedQuestionsOnly ? "Show All Questions" : "Show Selected Questions"}
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:w-[300px]">
                  <input type="number" step="0.5" placeholder="Marks Filter" value={questionFilter.marks} onChange={(e) => { setShowSelectedQuestionsOnly(false); setQuestionFilter((p) => ({ ...p, marks: e.target.value })); }} className={masterInput} />
                  <input type="number" step="0.25" placeholder="Negative Filter" value={questionFilter.negativeMarks} onChange={(e) => { setShowSelectedQuestionsOnly(false); setQuestionFilter((p) => ({ ...p, negativeMarks: e.target.value })); }} className={masterInput} />
                </div>
              </div>
              <div className="border border-slate-200 rounded-xl overflow-hidden bg-white" ref={listMathRef}>
                <div className="max-h-[35vh] overflow-auto custom-scrollbar">
                  <table className="w-full text-center text-xs">
                    <thead>
                      <tr className="bg-slate-100 text-slate-700 font-bold uppercase text-[10px]">
                        <th className="p-2">Select</th>
                        <th className="p-2">Q.ID</th>
                        <th className="p-2">Topic</th>
                        <th className="p-2">SubTopic</th>
                        <th className="p-2">Nested Topic</th>
                        <th className="p-2">Nested SubTopic</th>
                        <th className="p-2">Question</th>
                        <th className="p-2">Marks</th>
                        <th className="p-2">Neg</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleQuestionBankRows.map((q) => {
                        const qbId = q.questionBankId || q._id;
                        const checked = form.questionIds.includes(qbId);
                        const targetKey = hasGlobalSelectionTarget
                          ? GLOBAL_TARGET_KEY
                          : (selectionTargets.length ? questionTargetKeyById.get(normalizeUpper(qbId)) || getQuestionTargetKey(q) : GLOBAL_TARGET_KEY);
                        const targetConfig = getTargetConfig(targetKey);
                        return (
                          <tr key={qbId} className="border-t border-slate-100 hover:bg-slate-50">
                            <td className="p-2">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => setForm((prev) => {
                                  if (checked) return { ...prev, questionIds: prev.questionIds.filter((x) => x !== qbId) };
                                  if (!editId) {
                                    if (!selectionTargetMap.has(targetKey) || targetConfig.selectionType !== "MANUAL") return prev;
                                    const selectedCount = getSelectedCountForTarget(targetKey, prev.questionIds);
                                    if (selectedCount >= (Number(targetConfig.questionCount) || 0)) {
                                      toastErrorOnce(`ONLY ${targetConfig.questionCount} QUESTIONS ALLOWED FOR ${selectionTargetMap.get(targetKey)?.title || "THIS SECTION"}`);
                                      return prev;
                                    }
                                  }
                                  return { ...prev, questionIds: [...prev.questionIds, qbId] };
                                })}
                                disabled={isViewOnly || (!editId && (!selectionTargetMap.has(targetKey) || targetConfig.selectionType !== "MANUAL"))}
                                className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                              />
                            </td>
                            <td className="p-2 font-bold text-blue-600">{q.questionBankId}</td>
                            <td className="p-2 text-slate-700">{q.topicName || "---"}</td>
                            <td className="p-2 text-slate-700">{q.subTopicName || "---"}</td>
                            <td className="p-2 text-slate-700">{q.nestedTopicName || "---"}</td>
                            <td className="p-2 text-slate-700">{q.nestedSubTopicName || "---"}</td>
                            <td className="p-2"><div className="whitespace-pre-wrap text-left" dangerouslySetInnerHTML={{ __html: toHtml(q.questionText || "---") }} /></td>
                            <td className="p-2 font-bold">{q.marks}</td>
                            <td className="p-2 font-bold">{q.negativeMarks}</td>
                          </tr>
                        );
                      })}
                      {visibleQuestionBankRows.length === 0 && (
                        <tr><td colSpan="9" className="p-6 text-center text-xs text-slate-400 italic">No questions found for selected filters.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between mt-3 pt-2 border-t">
              <span />
              {/* {!isViewOnly && (
                <button disabled={saving} className={masterFormBtnSubmit}>
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                  <span>{editId ? "Update Set" : "Create Set"}</span>
                </button>
              )} */}
            </div>
          </form>
        </section>
      )}
  {showVisibilityModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-sm p-5 space-y-4">
            <div>
              <h3 className="text-sm font-bold uppercase text-slate-900">Select Visibility Type</h3>
              <p className="text-xs text-slate-500 mt-0.5">Choose how this set should appear to students.</p>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => selectForStudentWithCategory("PRACTICE")}
                className="flex flex-col items-center justify-center p-3 rounded-xl border border-slate-200 bg-slate-50 hover:bg-blue-50 hover:border-blue-500 text-slate-700 hover:text-blue-700 font-bold text-xs uppercase transition gap-1"
              >
                <span>Practice Set</span>
              </button>

              <button
                type="button"
                onClick={() => selectForStudentWithCategory("MOCK")}
                className="flex flex-col items-center justify-center p-3 rounded-xl border border-slate-200 bg-slate-50 hover:bg-emerald-50 hover:border-emerald-500 text-slate-700 hover:text-emerald-700 font-bold text-xs uppercase transition gap-1"
              >
                <span>Mock Test</span>
              </button>
            </div>

            <div className="flex justify-end pt-2 border-t border-slate-100">
              <button
                type="button"
                onClick={() => { setShowVisibilityModal(false); setSelectedRowForVisibility(null); }}
                className="px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-100 text-xs font-bold uppercase transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {showPublishModal && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
    <div className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-md p-5 space-y-4">
      <div>
        <h3 className="text-sm font-bold uppercase text-slate-900">Configure Mock Test Schedule</h3>
        <p className="text-xs text-slate-500 mt-0.5">Set the release date, time, and test duration.</p>
      </div>

      <div className="space-y-3">
        <div className={masterFormFieldGroup}>
          <label className={masterInputLabel}>Published Date</label>
          <input 
            type="date" 
            value={publishForm.testDate} 
            onChange={(e) => setPublishForm((p) => ({ ...p, testDate: e.target.value }))} 
            className={masterInput} 
          />
        </div>

        <div className={masterFormFieldGroup}>
          <label className={masterInputLabel}>Time</label>
          <input 
            type="time" 
            value={publishForm.testTime} 
            onChange={(e) => setPublishForm((p) => ({ ...p, testTime: e.target.value }))} 
            className={masterInput} 
          />
        </div>

        <div className={masterFormFieldGroup}>
          <label className={masterInputLabel}>Duration (Minutes)</label>
          <input 
            type="number" 
            min="1" 
            value={publishForm.durationMinutes} 
            onChange={(e) => setPublishForm((p) => ({ ...p, durationMinutes: e.target.value }))} 
            className={masterInput} 
          />
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
        <button
          type="button"
          onClick={() => { setShowPublishModal(false); setSelectedRowForPublish(null); }}
          className="px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-100 text-xs font-bold uppercase transition"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSavePublishSettings}
          className={`${masterFormBtnSubmit} px-4 py-1.5 text-xs`}
        >
          Publish Test
        </button>
      </div>
    </div>
  </div>
)}
    </div>
  );
};

export default MockTestMaster;
